import "@shoelace-style/shoelace/dist/components/range/range.js";
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/checkbox/checkbox.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";

import { css, html } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { Style } from "../Styles";
import { BaseElement } from "../BaseElement";
import {
    MapTileCacheOptions,
    MapTileCacher,
    defaultMapTileCacheOptions,
} from "../../MapTileCacher";
import { AllMapTypes, MapType, TileLayerDefinitions } from "../../TileLayers";
import { watch } from "../internal/watch";
import { modify } from "../../Utils";
import { litIf } from "../LitUtils";
import { MapBounds } from "../../Kernel";
import clone from "lodash-es/clone.js";
import isEqual from "lodash-es/isEqual.js";
import { PlaceTable } from "../../backend/cloud/PlaceTable";

@customElement("ds-map-cache-places-control")
export class MapCachePlacesControl extends BaseElement {
    static override styles = [
        Style,
        css`
            .container {
                display: flex;
                flex-direction: column;
                gap: 1em;
                width: 100%;
                overflow-x: hidden;
            }

            sl-card {
                width: 100%;
            }

            sl-select {
                width: 100%;
            }
            sl-range {
                width: 100%;
            }
            sl-button {
                width: 100%;
            }
        `,
    ];

    @property({ type: String }) cacheType = "around-places";

    @state() protected _settings = defaultMapTileCacheOptions();
    @state() protected _mapType: MapType = "satellite";
    @state() protected _numTiles = 0;
    @state() protected _spaceMb = 0;
    @state() protected _mapBounds: MapBounds | undefined;
    @state() protected _cacheMaxSizeExceeded = false;
    @state() protected _preparingDownload = false;
    @state() protected _maxZoom = this._currentLayerMaxZoom();
    @state() protected _currentZoom = this._maxZoom;

    private _lastMapBounds: MapBounds | undefined;
    private _lastSettings: MapTileCacheOptions | undefined;
    private _isConnected = false;

    @watch("_mapType")
    _mapTypeChanged() {
        this._maxZoom = this._currentLayerMaxZoom();
        this._currentZoom = this._currentLayerMaxZoom();
        this._updateSettings({
            maxZoom: this._maxZoom,
        });
    }

    @watch("_settings")
    public settingsChanged() {
        if (this._isConnected) {
            this._update();
        }
    }

    private _currentLayerMaxZoom(): number {
        return TileLayerDefinitions.get(this._mapType)!.options.maxNativeZoom as number;
    }

    private async _update() {
        if (
            isEqual(this._lastSettings, this._settings) &&
            isEqual(this._lastMapBounds, this._mapBounds)
        ) {
            return;
        }

        this._spaceMb = this._numTiles = 0;
        const def = TileLayerDefinitions.get(this._mapType);
        if (def) {
            const cacher = new MapTileCacher(def, this._settings);
            const filter = this._kernel.currentPlaceFilter.get();
            if (this.cacheType === "around-places") {
                const allPlaces = this._kernel.dataStore
                    .getSource<PlaceTable>("places")
                    .allFilteredItems(filter)
                    .filter((x) => x.data.locationKnown)
                    .map((x) => x.data.location);
                this._numTiles = cacher.adaptiveCacheGetTiles(allPlaces).length;
            } else if (this._mapBounds) {
                // const distAlongBounds = distanceInBetweenLocationsMeters(this._mapBounds.ne, this._mapBounds.sw);

                const result = await cacher.cacheWithinBoundsGetNumTiles(
                    this._mapBounds.sw,
                    this._mapBounds.ne
                );
                this._numTiles = result.numTiles;
                this._cacheMaxSizeExceeded = result.maxTilesExceeded;
            }
            this._spaceMb = Math.floor((this._numTiles * 25) / 1024);
        }

        this._lastSettings = clone(this._settings);
        this._lastMapBounds = clone(this._mapBounds);
    }

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._mapBounds = this._kernel.getMapBounds();
        await this._update();
        this._isConnected = true;
    }

    private _updateSettings(props: any) {
        this._settings = modify(this._settings, props);
    }

    private async _startCaching() {
        const def = TileLayerDefinitions.get(this._mapType);
        if (def) {
            this._preparingDownload = true;
            const cacher = new MapTileCacher(def, this._settings);
            if (this.cacheType === "around-places") {
                await cacher.updateCacheAroundPlaces();
            } else if (this.cacheType === "viewport") {
                if (this._mapBounds) {
                    await cacher.cacheWithinBounds(this._mapBounds.sw, this._mapBounds.ne);
                }
            }
            this._preparingDownload = false;
            this._raiseEvent("ds-download-started");
        }
    }

    override render() {
        return html`<sl-dialog ?open=${this._preparingDownload} label="Preparing download"
                ><div>
                    <sl-spinner class="loading-spinner"></sl-spinner>
                </div>
            </sl-dialog>
            <div class="container">
                <span>Cache within the current map view</span>
                <small>Ensure you have high detailed tiles when exploring offline.</small>
                <sl-select
                    hoist
                    .value=${this._mapType}
                    @sl-change=${(e: any) => {
                        this._mapType = e.target?.value.slice() ?? "satellite";
                    }}
                >
                    ${AllMapTypes.map(
                        (x) =>
                            html`<sl-option value=${x}
                                >${TileLayerDefinitions.get(x)?.name}</sl-option
                            >`
                    )}
                </sl-select>
                <sl-range
                    .label=${`Max zoom (${this._maxZoom})`}
                    min="0"
                    .max=${this._maxZoom}
                    .value=${this._currentZoom}
                    @sl-change=${(e: any) => {
                        this._currentZoom = Number(e.target.value);
                        this._updateSettings({
                            maxZoom: this._currentZoom,
                        });
                    }}
                ></sl-range>

                ${litIf(
                    this.cacheType === "around-places",
                    () => html`
                        <sl-checkbox
                            ?checked=${this._settings.adaptiveCache}
                            @sl-change=${(e: any) =>
                                this._updateSettings({
                                    adaptiveCache: e.target.checked,
                                })}
                            >Cache details around places?</sl-checkbox
                        >

                        <sl-range
                            label="Radius around location to cache"
                            min="100"
                            max="1000"
                            step="100"
                            .value=${this._settings.radiusMeters}
                            ?disabled=${!this._settings.adaptiveCache}
                            @sl-change=${(e: any) => {
                                this._updateSettings({
                                    radiusMeters: Number(e.target.value),
                                });
                            }}
                        ></sl-range>
                    `
                )}
                ${litIf(
                    this._cacheMaxSizeExceeded,
                    () =>
                        html`<strong
                            >Current settings exceed cache size limit. Try reducing max zoom or
                            zooming in on the map.</strong
                        > `,
                    () =>
                        html`<span
                                >Would cache ${this._numTiles} tiles, taking up to
                                ${this._spaceMb}mb</span
                            >
                            <sl-button @click=${() => this._startCaching()}
                                >Cache tiles</sl-button
                            > `
                )}
            </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-map-cache-places-control": MapCachePlacesControl;
    }
}
